import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../lib/colors';
import { Col, Row } from '../../lib/utils';
import { HoverImage } from '../../lib/styled';
import { I_LEFT_BRACKET, I_POPUP_EXIT, I_RIGHT_BRACKET } from '../../types/images';
import Scrollbars from 'react-custom-scrollbars-2';
import _ from 'lodash';

const FullscreenOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
`;

type Props = {
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    pictureList: any;
    initialIndex?: number;
};

const ModalImagesViewer = ({ setModalVisible, pictureList, initialIndex }: Props) => {
    const scrollbarsRef = useRef<any>(null);
    const [currentIndex, setCurrentIndex] = useState<number>(initialIndex || 0);

    const onClickChangePicture = (index: number) => {
        setCurrentIndex(index);
    };

    const onClickNextImage = () => {
        if (currentIndex > pictureList.length - 2) {
            return;
        } else {
            setCurrentIndex((prev) => prev + 1);
            if (scrollbarsRef.current) {
                const currentScrollTop = scrollbarsRef.current.getScrollTop();
                const targetScrollTop = currentScrollTop + 60;
                smoothScroll(scrollbarsRef.current, currentScrollTop, targetScrollTop);
            }
        }
    };

    const onClickPrevImage = () => {
        if (currentIndex === 0) {
            return;
        } else {
            setCurrentIndex((prev) => prev - 1);
            if (scrollbarsRef.current) {
                const currentScrollTop = scrollbarsRef.current.getScrollTop();
                const targetScrollTop = currentScrollTop - 60;
                smoothScroll(scrollbarsRef.current, currentScrollTop, targetScrollTop);
            }
        }
    };

    const smoothScroll = (scrollbar: any, from: number, to: number) => {
        const startTime = performance.now();
        const duration = 250;
        const step = (timestamp: number) => {
            const elapsed = timestamp - startTime;
            const fraction = Math.min(elapsed / duration, 1);
            const ease = fraction * (2 - fraction);
            scrollbar.scrollTop(from + (to - from) * ease);
            if (fraction < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setModalVisible(false);
            else if (e.key === 'ArrowRight') onClickNextImage();
            else if (e.key === 'ArrowLeft') onClickPrevImage();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex]);

    const currentItem = pictureList[currentIndex];
    const isVideo = currentItem?.contentType === 'VIDEO';

    return (
        <FullscreenOverlay onClick={() => setModalVisible(false)}>
            <div
                className="flex items-center justify-center w-full h-full"
                onClick={(e) => e.stopPropagation()}
            >
                {/* 닫기 버튼 */}
                <HoverImage
                    src={I_POPUP_EXIT}
                    style={{
                        width: 40,
                        height: 40,
                        position: 'absolute',
                        top: 24,
                        right: 24,
                        filter: 'brightness(10)',
                        cursor: 'pointer',
                        zIndex: 10,
                    }}
                    onClick={() => setModalVisible(false)}
                />

                {/* 이전 버튼 */}
                <div
                    onClick={onClickPrevImage}
                    className="flex items-center justify-center cursor-pointer absolute left-[24px]"
                    style={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        backgroundColor: currentIndex > 0 ? 'rgba(255,255,255,0.15)' : 'transparent',
                        transition: 'background 0.2s',
                        zIndex: 10,
                    }}
                >
                    {currentIndex > 0 && (
                        <HoverImage src={I_LEFT_BRACKET} style={{ width: 14, height: 44, filter: 'brightness(10)' }} />
                    )}
                </div>

                {/* 메인 콘텐츠 (이미지 또는 비디오) */}
                <div className="flex items-center justify-center" style={{ maxWidth: 'calc(100vw - 220px)', maxHeight: 'calc(100vh - 80px)' }}>
                    {isVideo ? (
                        <video
                            key={currentItem?._id || currentIndex}
                            src={currentItem?.content}
                            controls
                            autoPlay
                            style={{
                                maxWidth: '100%',
                                maxHeight: 'calc(100vh - 80px)',
                                objectFit: 'contain',
                                borderRadius: 8,
                            }}
                        />
                    ) : (
                        <img
                            src={currentItem?.content}
                            style={{
                                maxWidth: '100%',
                                maxHeight: 'calc(100vh - 80px)',
                                objectFit: 'contain',
                                borderRadius: 8,
                            }}
                        />
                    )}
                </div>

                {/* 다음 버튼 */}
                <div
                    onClick={onClickNextImage}
                    className="flex items-center justify-center cursor-pointer absolute right-[120px]"
                    style={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        backgroundColor: currentIndex < pictureList.length - 1 ? 'rgba(255,255,255,0.15)' : 'transparent',
                        transition: 'background 0.2s',
                        zIndex: 10,
                    }}
                >
                    {currentIndex < pictureList.length - 1 && (
                        <HoverImage src={I_RIGHT_BRACKET} style={{ width: 14, height: 44, filter: 'brightness(10)' }} />
                    )}
                </div>

                {/* 우측 썸네일 사이드바 */}
                <div
                    className="absolute right-0 top-0 h-full flex flex-col items-center pt-[80px] pb-[24px]"
                    style={{ width: 100, backgroundColor: 'rgba(0,0,0,0.4)' }}
                >
                    {/* 카운터 */}
                    <div className="text-white text-[13px] mb-[12px] opacity-70">
                        {currentIndex + 1} / {pictureList.length}
                    </div>

                    <Scrollbars
                        ref={scrollbarsRef}
                        autoHide
                        autoHideTimeout={800}
                        autoHideDuration={200}
                        style={{ width: 92, flex: 1 }}
                    >
                        <Col style={{ width: 76, paddingLeft: 8 }}>
                            {pictureList &&
                                pictureList.map((item: any, index: number) => {
                                    const itemIsVideo = item?.contentType === 'VIDEO';
                                    const thumbSrc = itemIsVideo ? (item?.thumbnail || item?.content) : item?.content;
                                    return (
                                        <Row
                                            key={'mediaDetail' + index}
                                            style={{
                                                width: 68,
                                                height: 68,
                                                marginTop: index === 0 ? 4 : 0,
                                                marginBottom: 8,
                                                borderRadius: 6,
                                                overflow: 'hidden',
                                                border: currentIndex === index ? '3px solid #3D73DD' : '3px solid transparent',
                                                cursor: 'pointer',
                                                opacity: currentIndex === index ? 1 : 0.6,
                                                transition: 'all 0.2s',
                                                position: 'relative',
                                            }}
                                            onClick={() => onClickChangePicture(index)}
                                        >
                                            <img src={thumbSrc} style={{ width: 68, height: 68, objectFit: 'cover' }} />
                                            {itemIsVideo && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: 'rgba(0,0,0,0.25)',
                                                    }}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                                        <polygon points="8,5 20,12 8,19" />
                                                    </svg>
                                                </div>
                                            )}
                                        </Row>
                                    );
                                })}
                        </Col>
                    </Scrollbars>
                </div>
            </div>
        </FullscreenOverlay>
    );
};

export default ModalImagesViewer;
